import { Alert, Box, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import { STATUS } from "@/constants";
import { AddCardsToAppDialog } from "@/features/app-studio/ui/AddCardsToAppDialog";
import { AddChartToAppDialog } from "@/features/app-studio/ui/AddChartToAppDialog";
import { AddTableToAppDialog } from "@/features/app-studio/ui/AddTableToAppDialog";
import { useTurnStream, type UseTurnStreamOptions } from "@/features/chat/hooks/useTurnStream";
import { getPrimarySqlFromQueries } from "@/features/chat/lib/chartSql";
import {
  addOptimisticTurn,
  completeTurn,
  loadSessionTurns,
  removeOptimisticTurn,
  resetChatSession,
  sendFirstTurn,
  sendFollowUpTurn,
  setTurnClarificationRequest,
  submitClarificationResponse,
  terminateTurn,
  terminateTurnLocally,
  updateTurnStatus,
  updateTurnStreamPreview,
} from "@/features/chat/model/chatSlice";
import type {
  CardDefinition,
  ClarificationAnswerItem,
  ClarificationRequest,
  GeneralizedChartDefinition,
} from "@/features/chat/model/chatTypes";
import { ChatAssistantResponse } from "@/features/chat/ui/ChatAssistantResponse";
import { ClarificationPanel } from "@/features/chat/ui/ClarificationOptionsPanel";
import { ChatHeader } from "@/features/chat/ui/ChatHeader";
import { ChatInputBar } from "@/features/chat/ui/ChatInputBar";
import { ChatProcessingPanel } from "@/features/chat/ui/ChatProcessingPanel";
import { ChatUserMessage } from "@/features/chat/ui/ChatUserMessage";
import { readChatLocationState } from "@/features/chat/model/chatNavigation";
import { useDomains } from "@/features/data-hub/hooks/useDomains";

function createClientTurnId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ChatPage() {
  const { t: tCommon } = useTranslation("common");
  const { t: tChat } = useTranslation("chat");
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams<{ sessionId?: string }>();
  const dispatch = useAppDispatch();
  const { domains } = useDomains();
  const { sessionId, turnId, domainId, sessionTitle, turns, sendStatus, loadStatus, error } =
    useAppSelector((state) => state.chat);
  const isSessionMode = Boolean(routeSessionId);
  const [selectedDomainId, setSelectedDomainId] = useState<number | "">("");
  const [inputValue, setInputValue] = useState("");
  const [activeStreamTurnId, setActiveStreamTurnId] = useState<string | null>(null);
  const [streamOptions, setStreamOptions] = useState<UseTurnStreamOptions | null>(null);
  const [pendingClientTurnId, setPendingClientTurnId] = useState<string | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [addChartDialogOpen, setAddChartDialogOpen] = useState(false);
  const [chartToAdd, setChartToAdd] = useState<GeneralizedChartDefinition | null>(null);
  const [sqlToAdd, setSqlToAdd] = useState<string | null>(null);
  const [addTableDialogOpen, setAddTableDialogOpen] = useState(false);
  const [tableSqlToAdd, setTableSqlToAdd] = useState<string | null>(null);
  const [addCardsDialogOpen, setAddCardsDialogOpen] = useState(false);
  const [cardsToAdd, setCardsToAdd] = useState<CardDefinition[]>([]);
  const [cardsSqlToAdd, setCardsSqlToAdd] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const consumedInitialQueryRef = useRef<number | null>(null);
  const pendingAutoSendRef = useRef<string | null>(null);
  const chatLocationState = readChatLocationState(location.state);
  const resetAt = chatLocationState?.resetAt;
  const initialQuery = chatLocationState?.initialQuery?.trim() ?? "";
  const launchDomainId = chatLocationState?.domainId;
  const handleClarificationRequired = useCallback(
    (request: ClarificationRequest, targetTurnId: string) => {
      dispatch(
        setTurnClarificationRequest({
          turnId: targetTurnId,
          clarificationRequest: request,
        }),
      );
    },
    [dispatch],
  );

  const {
    verboseLog,
    currentVerboseStep,
    streamedText,
    followUpQuestions,
    clarificationRequest: streamClarificationRequest,
    hasReceivedToken,
    status: streamStatus,
    error: streamError,
    summary: streamSummary,
    insights: streamInsights,
    why: streamWhy,
    action: streamAction,
    businessImpact: streamBusinessImpact,
  } = useTurnStream(activeStreamTurnId, {
    ...streamOptions,
    onClarificationRequired: handleClarificationRequired,
  });
  const isSending = sendStatus === STATUS.LOADING;
  const isStreaming =
    streamStatus === "connecting" ||
    streamStatus === "streaming" ||
    streamStatus === "waiting_for_clarification";
  const hasWaitingClarificationTurn = turns.some(
    (turn) => turn.status === "waiting_for_clarification",
  );
  const isProcessing = isSending || isStreaming || hasWaitingClarificationTurn;
  const effectiveDomainId = isSessionMode ? (domainId ?? selectedDomainId) : selectedDomainId;

  const handleAddChartToApp = useCallback(
    (chart: GeneralizedChartDefinition, sql: string | null) => {
      setChartToAdd(chart);
      setSqlToAdd(sql);
      setAddChartDialogOpen(true);
    },
    [],
  );

  const handleCloseAddChartDialog = useCallback(() => {
    setAddChartDialogOpen(false);
    setChartToAdd(null);
    setSqlToAdd(null);
  }, []);

  const handleAddTableToApp = useCallback((sql: string | null) => {
    setTableSqlToAdd(sql);
    setAddTableDialogOpen(true);
  }, []);

  const handleCloseAddTableDialog = useCallback(() => {
    setAddTableDialogOpen(false);
    setTableSqlToAdd(null);
  }, []);

  const handleAddCardsToApp = useCallback((cards: CardDefinition[], sql: string | null) => {
    setCardsToAdd(cards);
    setCardsSqlToAdd(sql);
    setAddCardsDialogOpen(true);
  }, []);

  const handleCloseAddCardsDialog = useCallback(() => {
    setAddCardsDialogOpen(false);
    setCardsToAdd([]);
    setCardsSqlToAdd(null);
  }, []);

  useEffect(() => {
    if (isSessionMode || domains.length === 0) {
      return;
    }
    if (typeof launchDomainId === "number") {
      if (selectedDomainId !== launchDomainId) {
        setSelectedDomainId(launchDomainId);
      }
      return;
    }
    if (selectedDomainId === "") {
      setSelectedDomainId(domains[0]?.id ?? "");
    }
  }, [domains, isSessionMode, launchDomainId, selectedDomainId]);

  useEffect(() => {
    if (resetAt) {
      setInputValue("");
      setActiveStreamTurnId(null);
      setStreamOptions(null);
      setPendingClientTurnId(null);
      dispatch(resetChatSession());
    }
  }, [dispatch, resetAt]);

  useEffect(() => {
    if (
      isSessionMode ||
      !initialQuery ||
      typeof launchDomainId !== "number" ||
      typeof resetAt !== "number" ||
      selectedDomainId !== launchDomainId ||
      consumedInitialQueryRef.current === resetAt
    ) {
      return;
    }

    consumedInitialQueryRef.current = resetAt;
    pendingAutoSendRef.current = initialQuery;
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    initialQuery,
    isSessionMode,
    launchDomainId,
    location.pathname,
    navigate,
    resetAt,
    selectedDomainId,
  ]);

  useEffect(() => {
    if (!isSessionMode || !routeSessionId) {
      return;
    }
    if (sessionId === routeSessionId && turns.length > 0) {
      return;
    }
    if (loadStatus === STATUS.LOADING || loadStatus === STATUS.FAILED) {
      return;
    }
    void dispatch(loadSessionTurns(routeSessionId));
  }, [dispatch, isSessionMode, loadStatus, routeSessionId, sessionId, turns.length]);

  useEffect(() => {
    if (isSessionMode && domainId !== null) {
      setSelectedDomainId(domainId);
    }
  }, [domainId, isSessionMode]);

  useEffect(() => {
    if (!isSessionMode || loadStatus !== STATUS.SUCCEEDED) {
      return;
    }
    if (activeStreamTurnId !== null || pendingClientTurnId !== null) {
      return;
    }

    const activeTurn = [...turns]
      .reverse()
      .find(
        (turn) =>
          turn.status === "pending" ||
          turn.status === "streaming" ||
          turn.status === "waiting_for_clarification",
      );
    if (!activeTurn) {
      return;
    }

    const initialVerboseLog = activeTurn.assistantData?.verboseLog ?? [];
    const maxStepIndex =
      initialVerboseLog.length > 0
        ? Math.max(...initialVerboseLog.map((step) => step.stepIndex))
        : 0;

    setStreamOptions({
      afterStepIndex: maxStepIndex,
      initialVerboseLog,
      initialStreamedText: activeTurn.assistantData?.text ?? "",
      initialFollowUpQuestions: activeTurn.assistantData?.followUpQuestions ?? [],
      initialClarificationRequest:
        activeTurn.clarificationRequest ?? activeTurn.assistantData?.clarificationRequest ?? null,
    });
    if (activeTurn.status !== "waiting_for_clarification") {
      setActiveStreamTurnId(activeTurn.turnId);
    }
  }, [
    activeStreamTurnId,
    isSessionMode,
    loadStatus,
    pendingClientTurnId,
    turns,
  ]);

  useEffect(() => {
    if (sendStatus !== STATUS.SUCCEEDED || turnId === null || pendingClientTurnId === null) {
      return;
    }

    setStreamOptions(null);
    setActiveStreamTurnId(turnId);
    setPendingClientTurnId(null);

    if (!isSessionMode && sessionId) {
      navigate(`/chat/${sessionId}`, { replace: true });
    }
  }, [isSessionMode, navigate, pendingClientTurnId, sendStatus, sessionId, turnId]);

  useEffect(() => {
    if (!activeStreamTurnId || streamStatus !== "streaming") {
      return;
    }

    const activeTurn = turns.find((turn) => turn.turnId === activeStreamTurnId);
    if (activeTurn?.status === "waiting_for_clarification") {
      return;
    }

    dispatch(
      updateTurnStatus({
        turnId: activeStreamTurnId,
        status: "streaming",
      }),
    );
  }, [activeStreamTurnId, dispatch, streamStatus, turns]);

  useEffect(() => {
    if (!activeStreamTurnId || streamStatus !== "waiting_for_clarification") {
      return;
    }

    dispatch(
      updateTurnStatus({
        turnId: activeStreamTurnId,
        status: "waiting_for_clarification",
      }),
    );

    if (streamClarificationRequest) {
      dispatch(
        setTurnClarificationRequest({
          turnId: activeStreamTurnId,
          clarificationRequest: streamClarificationRequest,
        }),
      );
    }
  }, [activeStreamTurnId, dispatch, streamClarificationRequest, streamStatus]);

  useEffect(() => {
    if (!activeStreamTurnId) {
      return;
    }

    if (
      streamStatus !== "streaming" &&
      streamStatus !== "connecting" &&
      streamStatus !== "waiting_for_clarification"
    ) {
      return;
    }

    if (verboseLog.length === 0 && !hasReceivedToken) {
      return;
    }

    dispatch(
      updateTurnStreamPreview({
        turnId: activeStreamTurnId,
        assistantData: {
          text: streamedText,
          followUpQuestions,
          verboseLog,
        },
      }),
    );
  }, [
    activeStreamTurnId,
    dispatch,
    followUpQuestions,
    hasReceivedToken,
    streamStatus,
    streamedText,
    verboseLog,
  ]);

  useEffect(() => {
    if (!activeStreamTurnId || streamStatus !== "complete") {
      return;
    }

    dispatch(
      completeTurn({
        turnId: activeStreamTurnId,
        assistantData: {
          text: streamedText,
          followUpQuestions,
          verboseLog,
          summary: streamSummary,
          insights: streamInsights,
          why: streamWhy,
          action: streamAction,
          businessImpact: streamBusinessImpact,
        },
      }),
    );
    setActiveStreamTurnId(null);
    setStreamOptions(null);

    if (sessionId) {
      void dispatch(loadSessionTurns(sessionId));
    }
  }, [
    activeStreamTurnId,
    dispatch,
    followUpQuestions,
    sessionId,
    streamAction,
    streamBusinessImpact,
    streamInsights,
    streamStatus,
    streamSummary,
    streamWhy,
    streamedText,
    verboseLog,
  ]);

  useEffect(() => {
    if (!activeStreamTurnId || streamStatus !== "terminated") {
      return;
    }

    dispatch(
      terminateTurnLocally({
        turnId: activeStreamTurnId,
        assistantData: {
          text: streamedText,
          followUpQuestions,
          verboseLog,
        },
      }),
    );
    setActiveStreamTurnId(null);
    setStreamOptions(null);

    if (sessionId) {
      void dispatch(loadSessionTurns(sessionId));
    }
  }, [
    activeStreamTurnId,
    dispatch,
    followUpQuestions,
    sessionId,
    streamStatus,
    streamedText,
    verboseLog,
  ]);

  useEffect(() => {
    if (!activeStreamTurnId || streamStatus !== "error") {
      return;
    }

    dispatch(
      updateTurnStatus({
        turnId: activeStreamTurnId,
        status: "error",
      }),
    );
    setActiveStreamTurnId(null);
    setStreamOptions(null);
  }, [activeStreamTurnId, dispatch, streamStatus]);

  const handleClarificationSubmit = useCallback(
    (targetTurnId: string, answers: ClarificationAnswerItem[]) => {
      const targetTurn = turns.find((turn) => turn.turnId === targetTurnId);
      const liveVerboseLog =
        activeStreamTurnId === targetTurnId && verboseLog.length > 0
          ? verboseLog
          : (targetTurn?.assistantData?.verboseLog ?? []);
      const maxStepIndex =
        liveVerboseLog.length > 0
          ? Math.max(...liveVerboseLog.map((step) => step.stepIndex))
          : 0;
      const clientMessageId = createClientTurnId();
      void dispatch(
        submitClarificationResponse({
          turnId: targetTurnId,
          answers,
          clientMessageId,
        }),
      ).then((result) => {
        if (submitClarificationResponse.fulfilled.match(result)) {
          setStreamOptions({
            afterStepIndex: maxStepIndex,
            initialVerboseLog: liveVerboseLog,
            initialStreamedText:
              activeStreamTurnId === targetTurnId
                ? streamedText
                : (targetTurn?.assistantData?.text ?? ""),
            initialFollowUpQuestions:
              activeStreamTurnId === targetTurnId
                ? followUpQuestions
                : (targetTurn?.assistantData?.followUpQuestions ?? []),
            resumeAfterClarification: true,
            reconnectKey: Date.now(),
          });
          setActiveStreamTurnId(result.payload.turn_id);
        }
      });
    },
    [activeStreamTurnId, dispatch, followUpQuestions, streamedText, turns, verboseLog],
  );

  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === "function") {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [turns, isProcessing, verboseLog, streamedText]);

  const handleSend = useCallback(
    (overrideQuestion?: string) => {
      const trimmed = (overrideQuestion ?? inputValue).trim();
      if (!trimmed || isProcessing) {
        return;
      }

      if (isSessionMode) {
        if (!routeSessionId) {
          return;
        }
        const clientTurnId = createClientTurnId();
        setPendingClientTurnId(clientTurnId);
        dispatch(addOptimisticTurn({ clientTurnId, userQuestion: trimmed }));
        setInputValue("");
        void dispatch(
          sendFollowUpTurn({
            sessionId: routeSessionId,
            userQuestion: trimmed,
            clientTurnId,
          }),
        );
        return;
      }

      if (typeof selectedDomainId !== "number") {
        return;
      }

      const clientTurnId = createClientTurnId();
      setPendingClientTurnId(clientTurnId);
      dispatch(addOptimisticTurn({ clientTurnId, userQuestion: trimmed }));
      setInputValue("");
      void dispatch(
        sendFirstTurn({
          domainId: selectedDomainId,
          userQuestion: trimmed,
          clientTurnId,
        }),
      );
    },
    [
      dispatch,
      inputValue,
      isProcessing,
      isSessionMode,
      routeSessionId,
      selectedDomainId,
    ],
  );

  useEffect(() => {
    const pendingQuery = pendingAutoSendRef.current;
    if (!pendingQuery || isProcessing) {
      return;
    }
    pendingAutoSendRef.current = null;
    handleSend(pendingQuery);
  }, [handleSend, isProcessing, selectedDomainId]);

  const handleStopGeneration = useCallback(async () => {
    const targetTurnId = activeStreamTurnId ?? turnId;
    if (!targetTurnId || isStopping) {
      return;
    }

    const previewAssistantData = {
      text: streamedText,
      followUpQuestions,
      verboseLog,
    };

    setIsStopping(true);
    try {
      await dispatch(terminateTurn(targetTurnId)).unwrap();
      dispatch(
        terminateTurnLocally({
          turnId: targetTurnId,
          assistantData: previewAssistantData,
        }),
      );
      setActiveStreamTurnId(null);
      setStreamOptions(null);
      setPendingClientTurnId(null);
      if (sessionId) {
        void dispatch(loadSessionTurns(sessionId));
      }
    } catch {
      dispatch(
        terminateTurnLocally({
          turnId: targetTurnId,
          assistantData: previewAssistantData,
        }),
      );
      setActiveStreamTurnId(null);
      setStreamOptions(null);
      setPendingClientTurnId(null);
      if (sessionId) {
        void dispatch(loadSessionTurns(sessionId));
      }
    } finally {
      setIsStopping(false);
    }
  }, [
    activeStreamTurnId,
    dispatch,
    followUpQuestions,
    isStopping,
    sessionId,
    streamedText,
    turnId,
    verboseLog,
  ]);

  useEffect(() => {
    if (sendStatus !== STATUS.FAILED || pendingClientTurnId === null) {
      return;
    }
    dispatch(removeOptimisticTurn({ clientTurnId: pendingClientTurnId }));
    setPendingClientTurnId(null);
  }, [dispatch, pendingClientTurnId, sendStatus]);

  const hasConversation = turns.length > 0 || isProcessing;
  const displayError = error ?? streamError;
  const trimmedSessionTitle = sessionTitle?.trim() ?? "";
  const headerTitle =
    isSessionMode && trimmedSessionTitle ? trimmedSessionTitle : tChat("title");

  const handleBackToHistory = useCallback(() => {
    navigate("/chat-history");
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
      }}
    >
      <Helmet>
        <title>{isSessionMode ? headerTitle : tCommon("routes.chat")}</title>
      </Helmet>

      <Box sx={{ flexShrink: 0 }}>
        <ChatHeader
          title={headerTitle}
          showBack={isSessionMode}
          onBack={handleBackToHistory}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: hasConversation ? "auto" : "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {hasConversation ? (
          <Stack spacing={2.5} sx={{ py: 1 }}>
            {turns.map((turn) => {
              const isActiveStreamTurn = activeStreamTurnId === turn.turnId;
              const isStreamInProgress =
                streamStatus === "connecting" || streamStatus === "streaming";
              const isWaitingForClarification =
                turn.status === "waiting_for_clarification" ||
                (isActiveStreamTurn && streamStatus === "waiting_for_clarification");
              const showTurnProcessing =
                ((pendingClientTurnId === turn.turnId && isSending) ||
                  (isActiveStreamTurn &&
                    (isStreamInProgress || streamOptions?.resumeAfterClarification === true))) &&
                !hasReceivedToken &&
                !isWaitingForClarification;
              const clarificationRequestForTurn =
                turn.clarificationRequest ??
                turn.assistantData?.clarificationRequest ??
                (isActiveStreamTurn ? streamClarificationRequest : null);
              const showClarificationPanel =
                isWaitingForClarification && clarificationRequestForTurn !== null;
              const showStreamingResponse =
                isActiveStreamTurn &&
                hasReceivedToken &&
                streamStatus !== "complete" &&
                streamStatus !== "terminated";
              const showCompletedResponse =
                turn.status === "complete" && turn.assistantData !== null;
              const showTerminatedResponse = turn.status === "terminated";
              const assistantData = showStreamingResponse
                ? {
                    text: streamedText,
                    followUpQuestions,
                    verboseLog,
                  }
                : turn.assistantData ??
                  (showTerminatedResponse
                    ? {
                        text: "",
                        followUpQuestions: [],
                        verboseLog: isActiveStreamTurn ? verboseLog : [],
                      }
                    : null);
              const primarySql = getPrimarySqlFromQueries(assistantData?.generatedQueries);
              const canAddChartToApp =
                !showStreamingResponse &&
                Boolean(primarySql && assistantData?.chartData?.charts?.length);
              const canAddTableToApp =
                !showStreamingResponse &&
                Boolean(primarySql && assistantData?.tabularData?.columns?.length);
              const responseCards = assistantData?.chartData?.cards ?? [];
              const canAddCardsToApp =
                !showStreamingResponse && Boolean(primarySql) && responseCards.length > 0;

              return (
                <Stack key={turn.turnId} spacing={2.5}>
                  <ChatUserMessage content={turn.userQuestion} />
                  {showTurnProcessing ? (
                    <ChatProcessingPanel
                      currentVerboseStep={currentVerboseStep}
                      verboseLog={verboseLog}
                    />
                  ) : null}
                  {showClarificationPanel && clarificationRequestForTurn ? (
                    <ClarificationPanel
                      request={clarificationRequestForTurn}
                      disabled={isSending}
                      onSubmit={(answers) => handleClarificationSubmit(turn.turnId, answers)}
                    />
                  ) : null}
                  {showStreamingResponse || showCompletedResponse || showTerminatedResponse ? (
                    <ChatAssistantResponse
                      text={assistantData?.text ?? ""}
                      summary={assistantData?.summary}
                      insights={assistantData?.insights}
                      why={assistantData?.why}
                      action={assistantData?.action}
                      businessImpact={assistantData?.businessImpact}
                      verboseLog={assistantData?.verboseLog ?? []}
                      followUpQuestions={assistantData?.followUpQuestions ?? []}
                      tabularData={assistantData?.tabularData}
                      chartData={assistantData?.chartData}
                      generatedQueries={assistantData?.generatedQueries}
                      answerConfidence={assistantData?.answerConfidence}
                      documentAgreement={assistantData?.documentAgreement}
                      documentConflicts={assistantData?.documentConflicts}
                      documentSources={assistantData?.documentSources}
                      documentSourceRefs={assistantData?.documentSourceRefs}
                      isStreaming={showStreamingResponse}
                      isTerminated={showTerminatedResponse}
                      onFollowUpClick={handleSend}
                      canAddChartToApp={canAddChartToApp}
                      onAddChartToApp={(chart) => handleAddChartToApp(chart, primarySql)}
                      canAddTableToApp={canAddTableToApp}
                      onAddTableToApp={() => handleAddTableToApp(primarySql)}
                      canAddCardsToApp={canAddCardsToApp}
                      onAddCardsToApp={() => handleAddCardsToApp(responseCards, primarySql)}
                      domainId={typeof effectiveDomainId === "number" ? effectiveDomainId : null}
                      turnId={turn.turnId}
                      feedback={turn.feedback}
                    />
                  ) : null}
                  {turn.status === "error" ? (
                    <Alert severity="error">{tChat("errorTitle")}</Alert>
                  ) : null}
                </Stack>
              );
            })}
            {displayError ? <Alert severity="error">{displayError}</Alert> : null}
            <Box ref={messagesEndRef} />
          </Stack>
        ) : null}
      </Box>

      <Box sx={{ flexShrink: 0, mt: hasConversation ? 2 : "auto" }}>
        <ChatInputBar
          domains={domains}
          selectedDomainId={typeof effectiveDomainId === "number" ? effectiveDomainId : ""}
          onDomainChange={setSelectedDomainId}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={() => handleSend()}
          onStopClick={handleStopGeneration}
          isProcessing={isProcessing}
          isStopping={isStopping}
          domainLocked={isSessionMode}
        />
      </Box>

      <AddChartToAppDialog
        open={addChartDialogOpen}
        onClose={handleCloseAddChartDialog}
        chart={chartToAdd}
        sql={sqlToAdd}
      />
      <AddTableToAppDialog
        open={addTableDialogOpen}
        onClose={handleCloseAddTableDialog}
        sql={tableSqlToAdd}
      />
      <AddCardsToAppDialog
        open={addCardsDialogOpen}
        onClose={handleCloseAddCardsDialog}
        cards={cardsToAdd}
        sql={cardsSqlToAdd}
      />
    </Box>
  );
}
